/**
 * Page transition presets. A preset is baked, not referenced: applying one
 * writes `_pageEnter` and `_pageExit` states on the target and `_pageExit`
 * assignments in the container's `pages`, and {@link detectPreset} reads the
 * preset back from the stored exit pose. Each write returns its undo command
 * without pushing it; null means nothing changed.
 */

import type { SceneObject } from '../../core/SceneObject';
import { Pages } from '../../behaviors/pages';
import { Properties } from '../../behaviors/properties';
import { PAGE_STATE_ENTER, PAGE_STATE_EXIT } from '../../scenedata/behaviors/pagesdata';
import type { StateDefinition } from '../../scenedata/behaviors/propertiesdata';
import type { UndoCommand } from '../undo/UndoCommand';
import { AddBehaviorCommand } from '../undo/AddBehaviorCommand';
import { BehaviorCommitCommand } from '../undo/BehaviorCommitCommand';
import { editRefusal } from '../authoring/checks';
import { bundle } from '../authoring/steps';
import { commitPages, pagingOf, takeBack } from './sharedChildren';
import { pageChildrenOf } from './pageContext';
import { foldNewPageIntoExitAssignments, withPresetAssignments } from './pagesModel';

export type PagePreset = 'slideLeft' | 'slideRight' | 'slideUp' | 'slideDown' | 'scaleUp';

/** A preset's pose in a state: `position` an offset from the target's authored position, `scale` absolute. */
export interface PresetPose {
    position?: [number, number, number];
    scale?: [number, number, number];
}

/** Each preset's label and its enter and exit poses. */
export const PAGE_PRESETS: Readonly<Record<PagePreset, { label: string; enter: PresetPose; exit: PresetPose }>> = {
    slideLeft: { label: 'Slide Left', enter: { position: [2, 0, 0] }, exit: { position: [-2, 0, 0] } },
    slideRight: { label: 'Slide Right', enter: { position: [-2, 0, 0] }, exit: { position: [2, 0, 0] } },
    slideUp: { label: 'Slide Up', enter: { position: [0, -1, 0] }, exit: { position: [0, 1, 0] } },
    slideDown: { label: 'Slide Down', enter: { position: [0, 1, 0] }, exit: { position: [0, -1, 0] } },
    scaleUp: { label: 'Scale Up', enter: { scale: [0, 0, 0] }, exit: { scale: [0, 0, 0] } },
};

const PRESETS = Object.keys(PAGE_PRESETS) as PagePreset[];

/** `pose` as a state stores it, for a target authored at `authored`: the position offset added to it. */
export function resolvePresetOffset(pose: PresetPose, authored: readonly [number, number, number]): PresetPose {
    const resolved: PresetPose = {};
    if (pose.position) resolved.position = [authored[0] + pose.position[0], authored[1] + pose.position[1], authored[2] + pose.position[2]];
    if (pose.scale) resolved.scale = [...pose.scale];
    return resolved;
}

/** The preset baked on `object`, by its `_pageExit` pose; '' when none matches. */
export function detectPreset(object: SceneObject): PagePreset | '' {
    const exit = object.getBehavior(Properties)?.getData().states?.[PAGE_STATE_EXIT]?.transform;
    if (!exit) return '';
    const authored = authoredPosition(object);
    return PRESETS.find(key => JSON.stringify(exit) === JSON.stringify(resolvePresetOffset(PAGE_PRESETS[key].exit, authored))) ?? '';
}

/**
 * Bake `preset` on `object`, content of one of the container's pages or a
 * page itself, or clear it with null: its two states, and its assignments
 * on every page. Null when nothing changed, the container pages its first
 * child's nodes (`firstChild`), or the scene can't be edited now (play, an
 * undo running).
 */
export async function applyPreset(container: SceneObject, object: SceneObject, preset: PagePreset | null): Promise<UndoCommand | null> {
    const pages = pagingOf(container);
    if (!pages || editRefusal(container.traceScene, [container, object])) return null;
    const steps: UndoCommand[] = [];
    try {
        await bakePreset(container, pages, object, preset, steps);
    } catch (error) {
        await takeBack(container, steps, error);
        throw error;
    }
    return steps.length > 0 ? bundle(`${preset ? 'Apply' : 'Clear'} ${object.name} transition`, steps) : null;
}

/** {@link applyPreset} on every page of the container, as one command. */
export async function applyDeckPreset(container: SceneObject, preset: PagePreset | null): Promise<UndoCommand | null> {
    const pages = pagingOf(container);
    if (!pages || editRefusal(container.traceScene, [container])) return null;
    const steps: UndoCommand[] = [];
    try {
        for (const page of pageChildrenOf(container)) await bakePreset(container, pages, page, preset, steps);
    } catch (error) {
        await takeBack(container, steps, error);
        throw error;
    }
    return steps.length > 0 ? bundle(`${preset ? 'Apply' : 'Clear'} page transitions`, steps) : null;
}

/**
 * For a page just added at `newIndex`: when another page carries a preset,
 * bake it on the new page too and assign it into the other pages, so the new
 * page doesn't appear without a transition. The new page's states need no
 * command, since undoing the add deletes the page; the returned one is the
 * container's `pages` change. Internal to `editor/pages`.
 */
export async function inheritDeckPreset(container: SceneObject, newIndex: number): Promise<UndoCommand | null> {
    const pages = container.getBehavior(Pages);
    const pageList = pageChildrenOf(container);
    const newPage = pageList[newIndex];
    const preset = pageList.map(page => (page === newPage ? '' : detectPreset(page))).find(found => found) ?? '';
    if (!pages || !newPage || !preset) return null;
    await writeStates(newPage, preset, []);
    const scene = container.traceScene;
    const presetTargets = pageList
        .filter(page => page.getBehavior(Properties)?.getData().states?.[PAGE_STATE_EXIT])
        .map(page => scene.getRelativePath(page, container));
    const before = pages.getData();
    const after = foldNewPageIntoExitAssignments(before.pages, pageList.map(page => page.name), newIndex, presetTargets);
    return commitPages(container, pages, { ...before, pages: after });
}

async function bakePreset(container: SceneObject, pages: Pages, object: SceneObject, preset: PagePreset | null, steps: UndoCommand[]): Promise<void> {
    await writeStates(object, preset, steps);
    const before = pages.getData();
    const target = container.traceScene.getRelativePath(object, container);
    const after = withPresetAssignments(before.pages, target, !!preset, pageChildrenOf(container).map(page => page.name));
    const command = await commitPages(container, pages, { ...before, pages: after });
    if (command) steps.push(command);
}

/** Write `preset`'s two states on `object`, keeping each state's values and transition, or remove them for null. Appends to `steps`. */
async function writeStates(object: SceneObject, preset: PagePreset | null, steps: UndoCommand[]): Promise<void> {
    let props = object.getBehavior(Properties);
    if (!props && !preset) return;
    if (!props) {
        props = await object.addBehavior(Properties);
        // The authored pose is Base from here on, whatever the live node shows.
        props.primeSavedTransform();
        steps.push(new AddBehaviorCommand(object, 'Properties', props.getData()));
    }
    const before = props.getData();
    const states = { ...before.states };
    if (preset) {
        const authored = authoredPosition(object);
        const state = (name: string, pose: PresetPose): StateDefinition => ({
            values: states[name]?.values ?? {},
            transform: resolvePresetOffset(pose, authored),
            transition: states[name]?.transition,
        });
        states[PAGE_STATE_ENTER] = state(PAGE_STATE_ENTER, PAGE_PRESETS[preset].enter);
        states[PAGE_STATE_EXIT] = state(PAGE_STATE_EXIT, PAGE_PRESETS[preset].exit);
    } else {
        if (!states[PAGE_STATE_ENTER] && !states[PAGE_STATE_EXIT]) return;
        delete states[PAGE_STATE_ENTER];
        delete states[PAGE_STATE_EXIT];
    }
    const after = Object.keys(states).length > 0 ? states : undefined;
    if (JSON.stringify(after) === JSON.stringify(before.states)) return;
    const errors = await props.commit({ states: after });
    if (errors.length > 0) throw new Error(`[pagePresets] Properties refused the patch: ${errors.map(e => e.message).join('; ')}`);
    steps.push(new BehaviorCommitCommand(object, 'Properties', before, props.getData()));
}

function authoredPosition(object: SceneObject): [number, number, number] {
    return object.getSceneObjectData().transform?.translation ?? [0, 0, 0];
}
